import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Input } from "../Input/Input";
import { Select } from "../Select/Select";
import { SearchableSelect } from "../SearchableSelect/SearchableSelect";
import { DateRangePicker } from "../DateRangePicker/DateRangePicker";
import styles from "./FilterBar.module.css";

export type FilterType = "text" | "dateRange" | "select" | "searchableSelect";

export interface FilterDef {
	key: string;
	type: FilterType;
	label: string;
	placeholder?: string;
	width?: number;
	options?: { value: string; label: string }[];
	uppercase?: boolean;
	disabled?: boolean | undefined;
}

interface FilterBarProps {
	filters: FilterDef[];
	values: Record<string, string>;
	onChange: (values: Record<string, string>) => void;
	stretch?: boolean;
}

const BASE_WIDTH: Record<FilterType, number> = {
	text: 200,
	select: 180,
	searchableSelect: 180,
	dateRange: 180,
};

export function FilterBar({ filters, values, onChange, stretch }: FilterBarProps) {
	const { t } = useTranslation();
	const displayValues = values;

	const update = (key: string, value: string) => {
		const next = { ...values, [key]: value };
		if (!value) delete next[key];
		onChange(next);
	};

	const clear = (f: FilterDef) => {
		const next = { ...values };
		if (f.type === "dateRange") {
			delete next[`${f.key}From`];
			delete next[`${f.key}To`];
		} else {
			delete next[f.key];
		}
		onChange(next);
	};

	const clearAll = () => {
		onChange({});
	};

	const summarize = (f: FilterDef): string => {
		if (f.type === "dateRange") {
			const from = displayValues[`${f.key}From`];
			const to = displayValues[`${f.key}To`];
			if (!from && !to) return "";
			if (from && to) return `${from} – ${to}`;
			return from ? `${from} →` : `→ ${to}`;
		}
		const v = displayValues[f.key];
		if (!v) return "";
		if (f.type === "select" || f.type === "searchableSelect") {
			return f.options?.find((o) => o.value === v)?.label ?? v;
		}
		return v;
	};

	const active = filters
		.map((f) => ({ filter: f, text: summarize(f) }))
		.filter((a) => a.text !== "");

	return (
		<div className={styles.container}>
			<div className={stretch ? `${styles.row} ${styles.stretch}` : styles.row}>
				{filters.map((f) => (
					<div
						key={f.key}
						className={styles.filter}
						style={
							stretch
								? { flex: `1 1 ${f.width ?? BASE_WIDTH[f.type]}px` }
								: f.type === "searchableSelect" && f.width
									? { width: f.width }
									: undefined
						}
					>
						<span className={styles.label}>{f.label}</span>
						{f.type === "text" && (
							<Input
								placeholder={f.placeholder ?? f.label}
								value={values[f.key] ?? ""}
								onChange={(e) => update(f.key, e.target.value)}
								className={styles.input}
								style={!stretch && f.width ? { width: f.width } : undefined}
							/>
						)}
						{f.type === "dateRange" && (
							<DateRangePicker
								from={values[`${f.key}From`] ?? ""}
								to={values[`${f.key}To`] ?? ""}
								onChange={(range) => {
									const next = { ...values };
									if (range.from) next[`${f.key}From`] = range.from;
									else delete next[`${f.key}From`];
									if (range.to) next[`${f.key}To`] = range.to;
									else delete next[`${f.key}To`];
									onChange(next);
								}}
								placeholder={f.placeholder ?? f.label}
							/>
						)}
						{f.type === "select" && f.options && (
							<Select
								value={values[f.key] ?? ""}
								onChange={(e) => update(f.key, e.target.value)}
								options={[{ value: "", label: t("all") }, ...f.options]}
								className={styles.select}
							/>
						)}
						{f.type === "searchableSelect" && f.options && (
							<SearchableSelect
								value={values[f.key] ?? ""}
								onChange={(value) => update(f.key, value)}
								options={f.options}
								placeholder={f.placeholder ?? t("all")}
								clearable
								uppercase={f.uppercase}
								disabled={f.disabled}
							/>
						)}
					</div>
				))}
			</div>
			{active.length > 0 && (
				<div className={styles.badges}>
					{active.map(({ filter: f, text }) => (
						<span key={f.key} className={styles.badge}>
							{f.label}: {text}
							<button type="button" onClick={() => clear(f)} className={styles.badgeClear}>
								<X size={12} />
							</button>
						</span>
					))}
					<button type="button" onClick={clearAll} className={styles.clearAll}>
						{t("clearAll")}
					</button>
				</div>
			)}
		</div>
	);
}
